import React, { useEffect, useState } from 'react';
import { Card, Tabs, Row, Col, Select, Button, message, Switch, Tag, Typography, List, Modal } from 'antd';
import { useQuery, useMutation, useLazyQuery, type ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { HolderOutlined } from '@ant-design/icons';
import { GET_BILL_TYPE_FEE_ITEM_CONFIGS, UPDATE_FEE_ITEM_ORDER, TOGGLE_FEE_ITEM_STATUS, ADD_FEE_ITEM_CONFIG, REMOVE_FEE_ITEM_CONFIG, GET_ALL_SETTLEMENT_FEE_ITEMS } from '../../../graphql/queries/settlement/billingConfigQueries';
import { GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, type AllSettlementClientResponse } from './graphql/queries/getAllSettlementClient';
import { BillTypeEnum } from '../../../graphql/types/settlement';
import type { SelectOptionType } from 'graphql/types/SelectOptionType';
import { settlementClientRoleTypeOptions, SettlementClientRoleType, type SettlementClientRoleTypeValue } from './config/settlementClientRoleType';
import { LIST_ALL_DSP_OPTIONS } from './graphql/queries/listAllDspOptions';
import { hasResource } from '../../../utils/resourceAuth';
import { ResourceCode } from '../../../constants/resourceCodes';
import InvoiceLineItemFeeItemMapTab from './components/InvoiceLineItemFeeItemMapTab';
import { BUSINESS_SCENARIO_ORDER, BUSINESS_SCENARIO_DSP, BUSINESS_SCENARIO_COST_PERIODIC, BUSINESS_SCENARIO_SORTING } from '../../../graphql/queries/settlement/getSettlementCommonQueries';
import { extractGraphQLMessageKey } from '../../../utils/graphqlUtils';

const { TabPane } = Tabs;
const { Option } = Select;
const { Text } = Typography;

interface BillTypeFeeItemConfigType {
  id: string;
  settlementClientId: string;
  billType: BillTypeEnum;
  feeItemId: string;
  feeItemName: string;
  feeItemCode: number;
  businessScenarioCode: number;
  isRequired: boolean;
  displayOrder: number;
  isEnabled: boolean;
  createdAt: string;
}

// 客户类型 -> 可配置的费用项业务场景，新增时供选择，列表也按此范围过滤（全局配置 clientId=0 由两类客户共用）
const FEE_ITEM_SCENARIO_OPTIONS_BY_ROLE: Record<SettlementClientRoleTypeValue, { value: number; label: string }[]> = {
  [SettlementClientRoleType.CHANNEL_CLIENT]: [
    { value: BUSINESS_SCENARIO_ORDER, label: '收入侧配送' },
    { value: BUSINESS_SCENARIO_SORTING, label: '收入侧分拣' },
  ],
  [SettlementClientRoleType.DSP_CLIENT]: [
    { value: BUSINESS_SCENARIO_DSP, label: 'DSP侧配送' },
    { value: BUSINESS_SCENARIO_COST_PERIODIC, label: '支出侧周期性计费' },
  ],
};

// 后端：同一客户 + 账单类型下该费用项已有生效配置，details.businessScenarioCode 为其所在场景
const FEE_ITEM_CONFIG_ALREADY_EXISTS_KEY = 'errors.business.settlement.feeItemConfigAlreadyExists';

const BillingConfigPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('fee-item-config');
  
  return (
    <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="费用项配置" key="fee-item-config">
            <FeeItemConfigTab />
          </TabPane>
          <TabPane tab="账单类型配置" key="bill-type-config">
            <div>账单类型配置功能待开发</div>
          </TabPane>
          <TabPane tab="Invoice费用项关联配置" key="invoice-line-item-fee-item-map">
            <InvoiceLineItemFeeItemMapTab />
          </TabPane>
        </Tabs>
      </Card>
  );
};

const FeeItemConfigTab: React.FC = () => {
  const { t } = useTranslation();
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  const [billType, setBillType] = useState<BillTypeEnum>();
  const [clientId, setClientId] = useState<string>('0'); // 默认选择全局配置
  const [clientRoleType, setClientRoleType] = useState<SettlementClientRoleTypeValue>(SettlementClientRoleType.CHANNEL_CLIENT);
  const [feeItems, setFeeItems] = useState<BillTypeFeeItemConfigType[]>([]);
  const [serverFeeItems, setServerFeeItems] = useState<BillTypeFeeItemConfigType[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFeeItemId, setSelectedFeeItemId] = useState<string>();
  const [selectedScenarioCode, setSelectedScenarioCode] = useState<number>();
  
  // 获取客户列表
  const { data: clientData, loading: clientLoading } = useQuery<AllSettlementClientResponse>(GET_ALL_SETTLEMENT_CLIENT_FOR_OPTIONS, {
    variables: { roleType: clientRoleType },
    fetchPolicy: 'cache-and-network'
  });

  const [loadDspOptions, { data: dspOptionsData, loading: dspOptionsLoading }] = useLazyQuery<{ allOptions: SelectOptionType[] }>(
    LIST_ALL_DSP_OPTIONS,
    { fetchPolicy: 'cache-and-network' }
  );
  
  const scenarioOptions = FEE_ITEM_SCENARIO_OPTIONS_BY_ROLE[clientRoleType];

  // 获取所选业务场景下的费用项（用于新增）
  const { data: feeItemData } = useQuery(GET_ALL_SETTLEMENT_FEE_ITEMS, {
    variables: { businessScenarioCode: selectedScenarioCode },
    skip: !selectedScenarioCode,
    fetchPolicy: 'cache-and-network'
  });
  
  // 获取费用项配置（不按场景查询，由前端按客户类型对应的场景范围过滤）
  const { data: configData, loading, refetch } = useQuery(GET_BILL_TYPE_FEE_ITEM_CONFIGS, {
    variables: { billType, clientId },
    skip: !billType || !clientId,
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    const configs: BillTypeFeeItemConfigType[] | undefined = configData?.getBillTypeFeeItemConfigs;
    if (configs) {
      // businessScenarioCode 为 Long 标量，可能被序列化为字符串，需转成 number 再比较
      const filtered = configs.filter(config =>
        FEE_ITEM_SCENARIO_OPTIONS_BY_ROLE[clientRoleType].some(option => option.value === Number(config.businessScenarioCode))
      );
      setFeeItems(filtered);
      setServerFeeItems(filtered);
      // 不在这里重置 hasUnsavedChanges，而是通过比较来计算
    }
  }, [configData, clientRoleType]);
  
  const [updateOrder] = useMutation(UPDATE_FEE_ITEM_ORDER);
  const [toggleStatus] = useMutation(TOGGLE_FEE_ITEM_STATUS);
  const [addFeeItemConfig] = useMutation(ADD_FEE_ITEM_CONFIG);
  const [removeFeeItemConfig] = useMutation(REMOVE_FEE_ITEM_CONFIG);
  // 动态计算是否有未保存的更改
  React.useEffect(() => {
    const hasChanges = JSON.stringify(feeItems.map(item => ({ id: item.id, displayOrder: item.displayOrder }))) !== 
                      JSON.stringify(serverFeeItems.map(item => ({ id: item.id, displayOrder: item.displayOrder })));
    setHasUnsavedChanges(hasChanges);
  }, [feeItems, serverFeeItems]);
  
  const handleClientChange = (newClientId: string | undefined) => {
    setClientId(newClientId || '0');
    // 重新获取数据
    if (billType && newClientId) {
      refetch({ billType, clientId: newClientId });
    } else if (billType) {
      refetch({ billType, clientId: '0' });
    } else {
      refetch();
    }
  };

  const handleClientRoleTypeChange = (value: SettlementClientRoleTypeValue) => {
    setClientRoleType(value);
    setClientId('0');
    if (billType) {
      refetch({ billType, clientId: '0' });
    }
  };

  const clientLabel = clientRoleType === SettlementClientRoleType.DSP_CLIENT ? 'DSP' : '客户(channel)';
  const clientOptions = [
    { value: '0', label: '全局默认配置' },
    ...(clientData?.allOptions || [])
  ];

  useEffect(() => {
    if (clientRoleType === SettlementClientRoleType.DSP_CLIENT) {
      loadDspOptions();
    }
  }, [clientRoleType, loadDspOptions]);
  
  const handleSave = async () => {
    try {
      const configs = feeItems.map((item, index) => ({
        id: item.id,
        displayOrder: index + 1
      }));
      
      await updateOrder({
        variables: {
          billType,
          configs
        }
      });
      
      // 保存成功后，更新 serverFeeItems 以反映新的排序
      setServerFeeItems([...feeItems]);
      message.success('保存成功');
      
      // 刷新数据
      await refetch();
    } catch (error) {
      message.error('保存失败');
    }
  };
  
  const handleToggleStatus = async (configId: string, enabled: boolean) => {
    try {
      await toggleStatus({
        variables: {
          configId,
          enabled
        }
      });
      
      setFeeItems(prev => prev.map(item => 
        item.id === configId ? { ...item, isEnabled: enabled } : item
      ));
      
      message.success('状态更新成功');
    } catch (error) {
      message.error('状态更新失败');
    }
  };
  
  const handleMoveUp = (index: number) => {
    if (index > 0) {
      const newItems = [...feeItems];
      [newItems[index], newItems[index - 1]] = [newItems[index - 1], newItems[index]];
      setFeeItems(newItems);
      setHasUnsavedChanges(true);
    }
  };
  
  const handleMoveDown = (index: number) => {
    if (index < feeItems.length - 1) {
      const newItems = [...feeItems];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      setFeeItems(newItems);
      setHasUnsavedChanges(true);
    }
  };

  const handleAddFeeItem = async () => {
    if (!billType || !selectedScenarioCode || !selectedFeeItemId) {
      message.error('请选择账单类型、业务场景和费用项');
      return;
    }

    try {
      const { data } = await addFeeItemConfig({
        variables: {
          clientId,
          billType,
          feeItemId: selectedFeeItemId,
          businessScenarioCode: selectedScenarioCode
        }
      });
      // 后端对费用项不存在等情况返回 false 而不抛错，必须检查返回值
      if (!data?.addFeeItemConfig) {
        message.error('新增费用项配置失败');
        return;
      }
      message.success('新增费用项配置成功');
      setShowAddModal(false);
      setSelectedScenarioCode(undefined);
      setSelectedFeeItemId(undefined);
      
      // 刷新数据，列表由 configData 的 effect 同步
      await refetch();
    } catch (error) {
      if (extractGraphQLMessageKey(error) === FEE_ITEM_CONFIG_ALREADY_EXISTS_KEY) {
        const details = (error as ApolloError).graphQLErrors[0]?.extensions?.details as { businessScenarioCode?: number } | undefined;
        message.error(t(FEE_ITEM_CONFIG_ALREADY_EXISTS_KEY, {
          scenarioName: getScenarioLabel(details?.businessScenarioCode ?? 0)
        }));
      } else {
        message.error('新增费用项配置失败');
      }
      console.error('新增费用项配置失败:', error);
    }
  };

  const handleRemoveFeeItem = async (configId: string) => {
    try {
      await removeFeeItemConfig({
        variables: { configId }
      });
      message.success('删除费用项配置成功');
      
      // 刷新数据，列表由 configData 的 effect 同步
      await refetch();
    } catch (error) {
      message.error('删除费用项配置失败');
      console.error('删除费用项配置失败:', error);
    }
  };
  
  return (
    <div>
      <Row gutter={16}>
        <Col span={8}>
          <Card title="客户类型">
            <Select
              value={clientRoleType}
              onChange={handleClientRoleTypeChange}
              options={settlementClientRoleTypeOptions}
              style={{ width: '100%' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card title={clientLabel}>
            <Select
              value={clientId}
              onChange={handleClientChange}
              placeholder={`请选择${clientLabel}`}
              style={{ width: '100%' }}
              options={clientOptions}
              showSearch
              optionFilterProp="label"
              loading={clientRoleType === SettlementClientRoleType.DSP_CLIENT ? dspOptionsLoading : clientLoading}
              allowClear
            >
            </Select>
          </Card>
        </Col>
        <Col span={8}>
          <Card title="账单类型选择">
            <Select 
              value={billType} 
              onChange={setBillType}
              placeholder="请选择账单类型"
              style={{ width: '100%' }}
            >
              <Option value={BillTypeEnum.DELIVERY_FEE_BILL}>运费账单</Option>
              <Option value={BillTypeEnum.SLA_BILL}>SLA账单</Option>
              <Option value={BillTypeEnum.DSP_DELIVERY_FEE_BILL}>DSP运费账单</Option>
              <Option value={BillTypeEnum.SORTING_BILL}>分拣账单</Option>
            </Select>
          </Card>
        </Col>
      </Row>
      <Row gutter={16} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card 
            title="费用项配置"
            extra={
              <div>
                {canOperate && (
                  <Button
                    type="primary"
                    onClick={() => {
                      setSelectedScenarioCode(scenarioOptions[0]?.value);
                      setShowAddModal(true);
                    }}
                    disabled={!billType}
                    style={{ marginRight: 8 }}
                  >
                    新增费用项
                  </Button>
                )}
                {canOperate && (
                  <Button
                    onClick={handleSave}
                    disabled={!hasUnsavedChanges}
                    loading={loading}
                  >
                    保存排序
                  </Button>
                )}
              </div>
            }
          >
            <FeeItemConfigList 
              feeItems={feeItems}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onToggleStatus={handleToggleStatus}
              onRemove={handleRemoveFeeItem}
              loading={loading}
            />
          </Card>
        </Col>
      </Row>
      
      {/* 新增费用项模态框 */}
      <Modal
        title="新增费用项配置"
        open={showAddModal}
        onOk={handleAddFeeItem}
        onCancel={() => {
          setShowAddModal(false);
          setSelectedScenarioCode(undefined);
          setSelectedFeeItemId(undefined);
        }}
        okText="确定"
        cancelText="取消"
      >
        <div>
          <p>客户: {clientId === '0' ? '全局默认配置' : clientData?.allOptions?.find((c: any) => c.value === clientId)?.label}</p>
          <p>
            账单类型: {billType === BillTypeEnum.DELIVERY_FEE_BILL
              ? '运费账单'
              : billType === BillTypeEnum.DSP_DELIVERY_FEE_BILL
                ? 'DSP运费账单'
                : billType === BillTypeEnum.SORTING_BILL
                  ? '分拣账单'
                  : 'SLA账单'}
          </p>
          <div style={{ marginTop: 16 }}>
            <label>业务场景:</label>
            <Select
              value={selectedScenarioCode}
              onChange={(value: number) => {
                setSelectedScenarioCode(value);
                setSelectedFeeItemId(undefined);
              }}
              placeholder="请选择业务场景"
              style={{ width: '100%', marginTop: 8 }}
              options={scenarioOptions}
            />
          </div>
          <div style={{ marginTop: 16 }}>
            <label>选择费用项:</label>
            <Select
              value={selectedFeeItemId}
              onChange={setSelectedFeeItemId}
              placeholder="请选择费用项"
              style={{ width: '100%', marginTop: 8 }}
            >
              {feeItemData?.allOptions?.map((feeItem: any) => (
                <Option key={feeItem.value} value={feeItem.value}>
                  {feeItem.label}
                </Option>
              ))}
            </Select>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const getScenarioLabel = (businessScenarioCode: number) => {
  const code = Number(businessScenarioCode);
  const option = Object.values(FEE_ITEM_SCENARIO_OPTIONS_BY_ROLE).flat().find(o => o.value === code);
  return option?.label ?? code;
};

interface FeeItemConfigListProps {
  feeItems: BillTypeFeeItemConfigType[];
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onToggleStatus: (configId: string, enabled: boolean) => void;
  onRemove: (configId: string) => void;
  loading: boolean;
}

const FeeItemConfigList: React.FC<FeeItemConfigListProps> = ({
  feeItems,
  onMoveUp,
  onMoveDown,
  onToggleStatus,
  onRemove,
  loading
}) => {
  const canOperate = hasResource([ResourceCode.menu.OPS_TOOL_SETTLEMENT]);
  if (loading) {
    return <div>加载中...</div>;
  }
  
  if (feeItems.length === 0) {
    return <div>暂无数据</div>;
  }
  
  return (
    <List
      dataSource={feeItems}
      renderItem={(item, index) => (
        <List.Item
                 actions={[
                   <Button
                     type="text"
                     icon={<HolderOutlined />}
                     onClick={() => onMoveUp(index)}
                     disabled={index === 0}
                     title="上移"
                   />,
                   <Button
                     type="text"
                     icon={<HolderOutlined />}
                     onClick={() => onMoveDown(index)}
                     disabled={index === feeItems.length - 1}
                     title="下移"
                   />,
                   <Switch
                     checked={item.isEnabled}
                     onChange={canOperate ? (checked) => onToggleStatus(item.id, checked) : undefined}
                     disabled={!canOperate}
                   />,
                   canOperate ? (
                     <Button
                       type="text"
                       danger
                       onClick={() => onRemove(item.id)}
                       title="删除"
                     >
                       删除
                     </Button>
                   ) : null
                 ]}
        >
          <List.Item.Meta
            title={
              <div>
                <Text strong>{item.feeItemName}</Text>
                <Tag style={{ marginLeft: 8 }}>{getScenarioLabel(item.businessScenarioCode)}</Tag>
                {item.isRequired && <Tag color="red" style={{ marginLeft: 8 }}>必须</Tag>}
              </div>
            }
            description={
              <div>
                <Text type="secondary">编码: {item.feeItemCode}</Text>
                <br />
                <Text type="secondary">排序: {item.displayOrder}</Text>
              </div>
            }
          />
        </List.Item>
      )}
    />
  );
};

export default BillingConfigPage;
